/**
 * Shared helpers for theme components.
 *
 * Ported from Shopify's Horizon theme (`assets/utilities.js`), © Shopify Inc.,
 * used under the theme license in LICENSE.md.
 *
 * Deliberately minimal. Horizon's version is 880 lines because it serves 80
 * components we did not port; copying it wholesale would ship dead bytes to
 * every page that loads a single component. Add a helper here the moment a
 * second file needs it — not before.
 */

/**
 * Schedules a callback during idle periods, or falls back to setTimeout.
 *
 * Safari only shipped requestIdleCallback in 16.4, so the fallback is not
 * theoretical.
 *
 * @type {(callback: IdleRequestCallback, options?: IdleRequestOptions) => number}
 */
export const requestIdleCallback =
  typeof window.requestIdleCallback === 'function'
    ? (callback, options) => window.requestIdleCallback(callback, options)
    : (callback) => window.setTimeout(callback, 0);

/**
 * Runs an action while a button shows it is working, and keeps the spinner on
 * screen long enough to be read.
 *
 * A fast `/cart/add.js` answers in under 100ms, and a spinner that appears and
 * vanishes inside one frame reads as a glitch rather than as a response — hence
 * the minimum.
 *
 * This is the last of the eight functions `product-buy-buttons.js` and
 * `product-sticky-buy.js` each had their own copy of. It lives here and not in
 * `@theme/cart` because there is nothing about carts in it.
 *
 * @template T
 * @param {HTMLButtonElement | null} button
 * @param {() => Promise<T>} run
 * @param {Object} [options]
 * @param {number} [options.minVisibleMs=300]
 * @param {() => void} [options.onSettled] - Runs once the button is usable
 *   again, to re-derive its label and state — the selected size may well have
 *   changed while the request was in flight.
 * @returns {Promise<T>}
 */
export async function withPendingButton(button, run, options = {}) {
  const { minVisibleMs = 300, onSettled } = options;

  if (!button) return run();

  const started = Date.now();

  button.classList.add('is-loading');
  button.setAttribute('aria-busy', 'true');
  button.setAttribute('aria-disabled', 'true');
  button.disabled = true;

  try {
    return await run();
  } finally {
    setTimeout(() => {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      button.disabled = false;
      onSettled?.();
    }, Math.max(0, minVisibleMs - (Date.now() - started)));
  }
}
